import { useCallback, useEffect, useRef, useState } from 'react';
import { PLANS, type EntitlementResponse } from '@repo/shared/plans';
import { formatDate } from '../labels.ts';
import {
  BillingRequestError,
  billingApi,
  forgetCheckout,
  loadEntitlement,
  needsRenewal,
  pendingCheckout,
  saveEntitlement,
  type Entitlement,
} from '../lib/entitlement.ts';

export interface BillingNotice {
  kind: 'success' | 'info' | 'error';
  text: string;
}

const RENEW_CHECK_MS = 30 * 60_000;
/** Bei diesen Fehlern bleibt ein begonnener Kauf gemerkt – alle anderen machen ihn ungültig. */
const RETRY_CLAIM = new Set(['checkout_incomplete', 'network', 'rate_limited', 'billing_failed']);

/**
 * Freischaltung dieses Geräts: übernimmt Käufe nach der Rückkehr von der Stripe-Bezahlseite und
 * erneuert Stripe-Freischaltungen rechtzeitig im Hintergrund.
 */
export function useEntitlement() {
  const [entitlement, setEntitlement] = useState<Entitlement | null>(loadEntitlement);
  const [notice, setNotice] = useState<BillingNotice | null>(null);

  const apply = useCallback((response: EntitlementResponse | null) => {
    setEntitlement(saveEntitlement(response));
  }, []);

  // Käufe abholen: nach der Rückkehr von Stripe (?checkout=success&session_id=…) – und beim Öffnen
  // der App, falls die Rückkehr woanders gelandet ist (App auf dem iPhone-Home-Bildschirm).
  const claiming = useRef(false);
  useEffect(() => {
    const claim = (sessionId: string, afterReturn: boolean) => {
      if (claiming.current) return;
      claiming.current = true;
      if (afterReturn) setNotice({ kind: 'info', text: 'Kauf wird bestätigt …' });
      billingApi
        .claim(sessionId)
        .then((response) => {
          forgetCheckout();
          apply(response);
          setNotice({ kind: 'success', text: purchaseText(response) });
        })
        .catch((error: unknown) => {
          const code = error instanceof BillingRequestError ? error.code : null;
          // Noch nicht bezahlt oder kurz keine Verbindung: beim nächsten Öffnen erneut versuchen.
          if (!code || !RETRY_CLAIM.has(code)) forgetCheckout();
          if (!afterReturn) return;
          const text =
            error instanceof Error ? error.message : 'Der Kauf konnte nicht bestätigt werden.';
          setNotice({ kind: 'error', text });
        })
        .finally(() => {
          claiming.current = false;
        });
    };
    const claimPending = () => {
      const sessionId = pendingCheckout();
      if (sessionId) claim(sessionId, false);
    };

    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout) {
      const sessionId = params.get('session_id');
      params.delete('checkout');
      params.delete('session_id');
      const query = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}`,
      );
      if (checkout === 'success' && sessionId) {
        claim(sessionId, true);
      } else {
        forgetCheckout();
        setNotice({ kind: 'info', text: 'Kauf abgebrochen. Es wurde nichts berechnet.' });
      }
    } else {
      claimPending();
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') claimPending();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [apply]);

  // Stripe-Freischaltungen gelten 24 Stunden – rechtzeitig erneuern.
  useEffect(() => {
    const renew = () => {
      const current = loadEntitlement();
      if (!current || !needsRenewal(current)) return;
      billingApi
        .renew(current.token)
        .then(apply)
        .catch((error: unknown) => {
          // Ungültige Tokens entfernen – Netzwerkfehler beim nächsten Durchlauf erneut versuchen.
          if (error instanceof BillingRequestError && error.code === 'invalid_token') apply(null);
        });
    };
    renew();
    const timer = window.setInterval(renew, RENEW_CHECK_MS);
    return () => window.clearInterval(timer);
  }, [apply]);

  const dismissNotice = useCallback(() => setNotice(null), []);
  return { entitlement, apply, notice, dismissNotice };
}

function purchaseText(response: EntitlementResponse): string {
  if (response.plan === 'free') return 'Der Kauf ist abgeschlossen, das Abo ist aber nicht aktiv.';
  const { name } = PLANS[response.plan];
  const trialEnd =
    response.subscription?.status === 'trialing' ? response.subscription.trialEnd : null;
  return trialEnd
    ? `${name} ist aktiv und bis ${formatDate(trialEnd)} kostenlos.`
    : `${name} ist aktiv.`;
}
